import { useCallback, useEffect } from "react";
import { Phone, MessageCircle } from "lucide-react";

import { useTranslation } from "./hooks/useTranslation";
import { WHATSAPP_URL } from "./data/config";

import { Navbar }         from "./sections/Navbar";
import { HeroSection }    from "./sections/HeroSection";
import { CoursesSection } from "./sections/CoursesSection";
import { MentorsSection } from "./sections/MentorsSection";
import { FreeSection }    from "./sections/FreeSection";
import { AboutSection }   from "./sections/AboutSection";
import { WhySection }     from "./sections/WhySection";
import { FaqSection }     from "./sections/FaqSection";
import { ContactSection } from "./sections/ContactSection";
import { ChatBot }        from "./components/ChatBot/ChatBot";

// ─── Helpers ─────────────────────────────────────────────────────────────────
const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

// ─── App ─────────────────────────────────────────────────────────────────────
export default function App() {
  const { lang, t, changeLang } = useTranslation();

  useEffect(() => {
    let link = document.querySelector("link[rel~='icon']");
    if (!link) { link = document.createElement("link"); document.head.appendChild(link); }
    link.rel = "icon"; link.type = "image/x-icon"; link.href = "/favicon.ico";
  }, []);

  const handleNav = useCallback((key) => { scrollTo(key); }, []);

  return (
    <div className="min-h-screen bg-white">

      <Navbar t={t} lang={lang} changeLang={changeLang} handleNav={handleNav} />

      <main>
        <HeroSection    t={t} lang={lang} handleNav={handleNav} />
        <CoursesSection t={t} lang={lang} />
        <MentorsSection t={t} lang={lang} />
        <FreeSection    t={t} />
        <AboutSection   t={t} lang={lang} />
        <WhySection     t={t} />
        <FaqSection     t={t} />
        <ContactSection t={t} lang={lang} />
      </main>

      <ChatBot lang={lang} />

      {/* ── WhatsApp Float ── */}
      <div className="fixed bottom-5 right-5 z-40">
        <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer"
          className="group relative flex items-center justify-center w-14 h-14 md:w-16 md:h-16 bg-green-500 hover:bg-green-600 rounded-full shadow-2xl transition-all duration-300 hover:scale-110"
          aria-label="Chat on WhatsApp">
          <MessageCircle className="w-8 h-8 text-white" />
          <span className="absolute right-full mr-3 top-1/2 -translate-y-1/2 bg-gray-900 text-white px-3 py-2 rounded-lg text-sm whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
            WhatsApp
            <span className="absolute left-full top-1/2 -translate-y-1/2 border-4 border-transparent border-l-gray-900" />
          </span>
        </a>
      </div>

      {/* ── Footer ── */}
      <footer className="bg-blue-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="flex flex-col items-center space-y-6">
            <img src="/images/codekloud_logo_web.png" alt="CodeKloud Academy"
              className="h-9 w-auto object-contain cursor-pointer" loading="lazy"
              onClick={() => handleNav("home")} />
            <div className="flex flex-col md:flex-row items-center gap-4 md:gap-6">
              <a href="tel:+994702607637" className="flex items-center gap-2 text-white/80 hover:text-white transition-colors">
                <Phone className="w-4 h-4" />+994 70 260 76 37
              </a>
              <a href="tel:+994502634543" className="flex items-center gap-2 text-white/80 hover:text-white transition-colors">
                <Phone className="w-4 h-4" />+994 50 263 45 43
              </a>
            </div>
            <p className="text-white/70 text-sm">© {new Date().getFullYear()} CodeKloud Academy. {t.footer.rights}</p>
          </div>
        </div>
      </footer>

    </div>
  );
}
